import { Fragment, useContext } from "react";
import { useTranslation } from "react-i18next";
import styles from "./sign-up-form-button.styles.module.scss";
import { SignUpFormContext } from "../sign-up-form.context";
import * as reactDeviceDetect from "react-device-detect";
import { AppContext } from "../../../../app.context";
import { OnboardingUser } from "../../../../app.constants";
import MobileWarningOverlayComponent from "../../../../ui/mobile-warning-overlay/mobile-warning-overlay.component";
import OnboardingOverlayComponent from "../../../../ui/onboarding-overlay/onboarding-overlay.component";
import { PrimaryButtonComponent, TypographyComponent } from "@gx1/gx-ui-common";
import { SignupTokenType } from "../../sign-up-v4.constants";
import { useSignupToken } from "../../hooks/use-signup-token.hook";

const SignUpButton = () => {
  const { t } = useTranslation();
  const {
    email,
    firstName,
    lastName,
    password,
    termsPolicyAgreement,
    isProcessing,
    setIsProcessing,
    isCompleteSignUpForm,
    isCompleteProfileForm,
    page,
    setPage,
    profile,
    setErrorText,
  } = useContext(SignUpFormContext);

  const {
    setShowMobileMessage,
    setOnboardingUser,
    setShowOnboardingExperience,
    showOnboardingExperience,
    showMobileMessage,
  } = useContext(AppContext);
  const { token, signupTokenType } = useSignupToken();

  const handleClick = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${window.gxApiConnection}/api/v7/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          password,
          termsPolicyAgreement,
          token,
          profile: signupTokenType === SignupTokenType.INVITE_SURVEY ? undefined : profile,
        }),
        credentials: "include",
      });
      const resJson: { data: { gcomUserAuthToken: string }, message?: string } = await response.json();
      if (response.ok) {
        localStorage.setItem("genie_uid", resJson.data.gcomUserAuthToken);
        await window.eventBus.publish("WS_CONNECT");
        await window.eventBus.publish("SESSION_EVENT_LOAD");
        await window.eventBus.publish("USER_STATE_LOAD");
        if (signupTokenType === SignupTokenType.INVITE_SURVEY) {
          setOnboardingUser(OnboardingUser.SURVEY_RECIPIENT);
        } else {
          setOnboardingUser(OnboardingUser.EVALUATOR);
        }
        if (reactDeviceDetect.isMobile) {
          setShowMobileMessage(true);
        } else {
          setShowOnboardingExperience(true);
        }
      } else if (response.status === 422 && resJson?.message === "PASSWORD_REQUIREMENTS_UNSATISFIED") {
        setErrorText(`${t("Passwords cannot contain words that were in a previous password breach.")}`);
      } else {
        throw new Error();
      }
    } catch (error) {
      setErrorText(`${t("Oops... something went wrong. Please try again.")}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeMobileMessage = () => {
    setShowOnboardingExperience(true);
  };

  return (
    <Fragment>
      <div className={styles.footerContainer}>
        <TypographyComponent styling={"p-sm"} color={"error-graphic"}>
          {`*${t("All fields are required")}`}
        </TypographyComponent>
        {page === 1 && signupTokenType !== SignupTokenType.INVITE_SURVEY ? (
          <PrimaryButtonComponent
            ariaLabel={t("next step button")}
            isDisabled={!isCompleteSignUpForm}
            isLoading={isProcessing}
            onClick={() => setPage(2)}
          >
            {t("Next: Complete Profile")}
          </PrimaryButtonComponent>
        ) : (
          <div className={styles.btnGetStarted}>
            <PrimaryButtonComponent
              ariaLabel={t("sign up button")}
              isDisabled={signupTokenType === SignupTokenType.INVITE_SURVEY ? !isCompleteSignUpForm : !isCompleteProfileForm}
              isLoading={isProcessing}
              onClick={handleClick}
            >
              {t("Get Started")}
            </PrimaryButtonComponent>
          </div>
        )}
      </div>
      <MobileWarningOverlayComponent isOpen={showMobileMessage} onClose={closeMobileMessage} />
      <OnboardingOverlayComponent isOpen={showOnboardingExperience} />
    </Fragment>
  );
};

export default SignUpButton;
